import React from 'react';
import { MDText } from 'i18n-react';

import { Button, CrossIcon } from '@motorway/mw-highway-code';

import cypressIds from 'Components/cypress_ids.json';
import savedSearchesTexts from 'Components/Manage/SavedSearches/SavedSearches.texts';
import SavedSearchesIntroStepList from 'Components/Manage/SavedSearches/SavedSearchesIntroStepList/SavedSearchesIntroStepList';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { triggerUserClickedShowVehiclesEvent } from 'Services/analytics/events';
import { useVehicleCount } from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import {
	useCurrentFilter,
	useSavedFilters,
	useSetIsSavedSearchPillOpen,
} from 'Stores/SavedFiltersStore/SavedFiltersStore';
import type { OnCloseEvent } from 'Types/drawer';
import { applyCypressData } from 'Utilities/helpers';
import { LOCAL_STORAGE_KEYS } from 'Utilities/helpers/localStorage.const';
import { getValueFromLocalStorage } from 'Utilities/helpers/localStorage.helpers';
import { useResetFilters } from 'Utilities/hooks/vehicleList/useResetFilters';

import { NewGradingInfoBox } from '../NewGradingInfoBox/NewGradingInfoBox';
import SavedSearchFooter from '../SavedSearchFooter/SavedSearchFooter';
import LocalTextsFooter from '../SavedSearchFooter/SavedSearchFooter.texts.json';

import type { SavedItem } from './SavedSearchItem';
import { SavedSearchItem } from './SavedSearchItem';
import { POPOVER_GAEVENT_CONSTS } from './SavedSearchPopoverContent.consts';
import { triggerSavedSearchPopoverGAEvent } from './SavedSearchPopoverContent.helpers';
import LocalTexts from './SavedSearchPopoverContent.texts.json';

import styles from './SavedSearchPopoverContent.module.scss';

const LocalT = new MDText(LocalTexts);
const LocalTFooter = new MDText(LocalTextsFooter);
const SavedSearchesT = new MDText(savedSearchesTexts);

interface OnCloseProps {
	analyticsTrigger?: Noop;
	event: OnCloseEvent;
	eventAction?: string;
}
interface SavedSearchPopoverContentProps {
	onCreateSavedSearchButtonClick?: Noop;
}
export const SavedSearchPopoverContent: React.FC<SavedSearchPopoverContentProps> = ({
	onCreateSavedSearchButtonClick,
}) => {
	const {
		minWidth: { breakpointContent: isDesktop },
	} = useBreakpoint();
	const savedFilters = useSavedFilters();
	const setIsSavedSearchPillOpen = useSetIsSavedSearchPillOpen();
	const currentFilter = useCurrentFilter();
	const vehicleCount = useVehicleCount();
	const vehiclePluralisation = vehicleCount !== 1 ? 's' : '';
	const showVehicleButtonText = String(
		LocalTFooter.translate('showVehicles', {
			count: vehicleCount,
			vehiclePluralisation,
		}),
	);
	const resetFilters = useResetFilters();
	const isEmptyState = !savedFilters?.length;
	const shouldShowSavedSearchesCount = isDesktop || !isEmptyState;
	const savedSearchPluralisation = savedFilters?.length !== 1 ? 'es' : '';

	const shouldClearFilter = (event: OnCloseEvent) => {
		const element = event?.target as HTMLElement;
		return currentFilter && element?.textContent !== showVehicleButtonText;
	};

	const onClose = ({ analyticsTrigger, event, eventAction }: OnCloseProps) => {
		if (eventAction) triggerSavedSearchPopoverGAEvent(eventAction);
		if (analyticsTrigger) analyticsTrigger();

		if (shouldClearFilter(event)) {
			resetFilters();
		}
		setIsSavedSearchPillOpen(false);
	};

	const onClickCreateSavedSearchButton = () => {
		triggerSavedSearchPopoverGAEvent(POPOVER_GAEVENT_CONSTS.createSavedSearchButton);
		if (onCreateSavedSearchButtonClick) {
			onCreateSavedSearchButtonClick();
		}
	};

	const hasDismissedGradingSavedSearchesExplanation =
		getValueFromLocalStorage(LOCAL_STORAGE_KEYS.GRADING_SAVED_SEARCHES_EXPLANATION_DISMISSED) === 'true';

	return (
		<div className={styles.container}>
			{isDesktop && (
				<span className={styles.closeButtonWrapper}>
					<Button
						icon={CrossIcon}
						label="close"
						onClick={(event: OnCloseEvent) => onClose({ event, eventAction: POPOVER_GAEVENT_CONSTS.closeButton })}
						showLabel={false}
						variant="tertiary"
						{...applyCypressData(cypressIds.buttons.savedSearchPopoverCloseButton)}
					/>
				</span>
			)}
			<h3 className={styles.title}>{LocalT.translate('title')}</h3>
			{shouldShowSavedSearchesCount && (
				<p className={styles.subTitle}>{`${savedFilters.length} ${LocalT.translate('subTitle', {
					savedSearchPluralisation,
				})}`}</p>
			)}
			<div className={styles.radioGroup} {...applyCypressData(cypressIds.buttons.savedSearchPopoverRadioGroup)}>
				{/*  DEP-979 - To remove  */}
				{!hasDismissedGradingSavedSearchesExplanation ? <NewGradingInfoBox /> : null}
				{savedFilters.map((savedItem: SavedItem) => (
					<SavedSearchItem key={savedItem.id} id={savedItem.id} name={savedItem.name} />
				))}
			</div>
			{isEmptyState && (
				<div className={styles.intro}>
					<SavedSearchesIntroStepList />
					{!isDesktop && (
						<div className={styles.introButton}>
							<Button
								as="a"
								data-thc-style-override="true"
								onClick={onClickCreateSavedSearchButton}
								{...applyCypressData(cypressIds.buttons.savedSearchIntroCreate)}
								label={SavedSearchesT.translate('empty.createSavedSearchButton')}
							/>
						</div>
					)}
				</div>
			)}
			{!isEmptyState && (
				<SavedSearchFooter
					onClose={(event: OnCloseEvent) => onClose({ analyticsTrigger: triggerUserClickedShowVehiclesEvent, event })}
					showVehicleButtonText={showVehicleButtonText}
				/>
			)}
		</div>
	);
};
